import React, { useState, useEffect } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ServiceHistoryButton } from "@/components/ServiceHistoryButton";
import { AdminCost, useSourceCosts } from "@/components/AdminSourceCost";
import { toast } from "sonner";
import { Home, MapPin, TrendingUp } from "lucide-react";

const TYPES = [
  { v: 20, label: "Residenziale (civile abitazione)" },
  { v: 21, label: "Residenziale di pregio" },
  { v: 10, label: "Ufficio" },
  { v: 5, label: "Negozio / Commerciale" },
  { v: 6, label: "Box / Garage" },
];

export default function RealEstate() {
  const [address, setAddress] = useState("");
  const [purpose, setPurpose] = useState("sale");
  const [type, setType] = useState("20");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [svcFee, setSvcFee] = useState(null);
  const { costs: sourceCosts } = useSourceCosts();
  useEffect(() => { api.get("/openapi/service-prices").then((r) => setSvcFee(r.data?.oa_realestate_valuation?.price ?? null)).catch(() => {}); }, []);

  const submit = async () => {
    if (!address.trim()) { toast.error("Inserisci l'indirizzo dell'immobile"); return; }
    setBusy(true); setRes(null);
    try {
      const { data } = await api.post("/openapi/realestate/valuation", { address: address.trim(), purpose, type: parseInt(type) });
      setRes(data);
      toast.success(`Valutazione ottenuta. Commissione ${euro(data.fee)}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nella valutazione");
    } finally { setBusy(false); }
  };

  const q = res?.quotazione || {};

  return (
    <div className="space-y-6 max-w-2xl" data-testid="realestate-page">
      <ServiceHistoryButton service="valutazioni" />
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Servizi pubblici</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Home className="w-7 h-7 text-primary" /> Valutazione immobiliare</h1>
        <p className="text-sm text-muted-foreground mt-1">Ottieni la quotazione al m² per un immobile in tempo reale.</p>
      </div>

      {svcFee != null && <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm" data-testid="re-fee">Costo del servizio: <strong>{euro(svcFee)}</strong> · <span className="text-muted-foreground">servizio una tantum (nessun rinnovo)</span> · scalato dal Plafond servizi alla richiesta. <AdminCost cost={sourceCosts?.openapi?.oa_realestate_valuation} testId="re-source-cost" /></div>}

      <Card className="p-5 glow-border space-y-3" data-testid="realestate-form-card">
        <div><Label>Indirizzo immobile *</Label><Input className="mt-1.5" data-testid="re-address-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="es. Via Roma 1, Milano, 20100" /></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2.5">
          <div>
            <Label>Finalità</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger className="mt-1.5" data-testid="re-purpose-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sale">Vendita</SelectItem>
                <SelectItem value="rent">Affitto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo immobile</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1.5" data-testid="re-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t.v} value={String(t.v)}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={submit} disabled={busy} className="gap-2" data-testid="re-submit-button">
          <TrendingUp className="w-4 h-4" /> {busy ? "Valutazione…" : (svcFee != null ? `Richiedi valutazione · ${euro(svcFee)}` : "Richiedi valutazione")}
        </Button>
      </Card>

      {res && (
        <Card className="p-6 glow-border space-y-4" data-testid="realestate-result-card">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><MapPin className="w-4 h-4 text-primary" /> {res.address} {res.citta ? `· ${res.citta}` : ""} {res.provincia ? `(${res.provincia})` : ""}</div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg border border-border p-4"><p className="text-xs text-muted-foreground">Minimo</p><p className="font-head text-xl font-bold" data-testid="re-min">{q.min != null ? `${euro(q.min)}/m²` : "—"}</p></div>
            <div className="rounded-lg border border-primary/50 p-4 bg-primary/5"><p className="text-xs text-primary">Medio</p><p className="font-head text-2xl font-bold" data-testid="re-med">{q.med != null ? `${euro(q.med)}/m²` : "—"}</p></div>
            <div className="rounded-lg border border-border p-4"><p className="text-xs text-muted-foreground">Massimo</p><p className="font-head text-xl font-bold" data-testid="re-max">{q.max != null ? `${euro(q.max)}/m²` : "—"}</p></div>
          </div>
          <p className="text-xs text-muted-foreground">{res.fascia ? `Fascia: ${res.fascia}. ` : ""}{q.anno ? `Rif. ${q.anno}${q.semestre ? " · sem. " + q.semestre : ""}.` : ""} {res.immobile?.label ? `Tipologia: ${res.immobile.label}.` : ""}</p>
        </Card>
      )}
    </div>
  );
}
